import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  BookOpen, Mail, Sparkles, Inbox, Plus, ExternalLink, ThumbsUp, ThumbsDown,
  Pencil, Trash2, Info, ShieldCheck, Clock, X, Check, Tag,
} from 'lucide-react';
import { fmtDateTime } from '@/lib/format';

const SUBMIT_SOP_ROLES = ['dm_creative', 'dm_technical', 'dm_architecture', 'operations', 'accounts', 'owner'];
const TEMPLATE_APPROVERS = ['owner', 'operations'];
const RESOURCE_MANAGERS = ['owner', 'operations'];
const DESIGN_SOP_ROLES = ['owner', 'dm_creative', 'dm_technical', 'dm_architecture', 'operations', 'team'];
const ACCOUNTS_SOP_ROLES = ['owner', 'accounts'];

export default function KnowledgeHubPage() {
  const { user } = useAuth();
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get('tab') || 'sops';
  const [tab, setTab] = useState(initialTab);

  const isOwner = user?.role === 'owner';
  const isTemplateApprover = TEMPLATE_APPROVERS.includes(user?.role);
  const canRecommendSop = SUBMIT_SOP_ROLES.includes(user?.role);
  const canManageResource = RESOURCE_MANAGERS.includes(user?.role);

  const tabs = useMemo(() => {
    const list = [
      { id: 'sops', label: 'SOPs', Icon: BookOpen },
      { id: 'templates', label: 'Email Templates', Icon: Mail },
      { id: 'resources', label: 'Design Resources', Icon: Sparkles },
      { id: 'how_to', label: 'How to use', Icon: Info },
    ];
    if (isOwner || isTemplateApprover) {
      list.splice(3, 0, { id: 'pending', label: 'Pending Approvals', Icon: Inbox });
    }
    return list;
  }, [isOwner, isTemplateApprover]);

  return (
    <AppShell breadcrumbs={[{ label: 'Knowledge Hub' }]}>
      <div className="p-6 max-w-[1200px] mx-auto space-y-4" data-testid="knowledge-hub-page">
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <BookOpen size={20} className="text-[hsl(var(--primary))]" />
            Knowledge Hub
          </h1>
          <p className="text-[13px] text-[hsl(var(--foreground-muted))]">
            SOPs, email templates, and design resources — all link-based and role-aware.
          </p>
        </div>

        <div className="flex items-center gap-1 flex-wrap border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`kh-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-[13px] transition-all border-b-2 -mb-px ${
                tab === t.id
                  ? 'border-[hsl(var(--primary))] text-[hsl(var(--foreground))] font-semibold'
                  : 'border-transparent text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]'
              }`}
            >
              <t.Icon size={13} />{t.label}
            </button>
          ))}
        </div>

        {tab === 'sops' && <SOPsSection canRecommend={canRecommendSop} userRole={user?.role} isOwner={isOwner} />}
        {tab === 'templates' && <TemplatesSection isApprover={isTemplateApprover} userId={user?.id} />}
        {tab === 'resources' && <ResourcesSection canManage={canManageResource} />}
        {tab === 'pending' && (isOwner || isTemplateApprover) && (
          <PendingApprovalsSection isOwner={isOwner} isTemplateApprover={isTemplateApprover} />
        )}
        {tab === 'how_to' && <HowToSection />}
      </div>
    </AppShell>
  );
}

// ------------------------------------------------------------------
// SOPs
// ------------------------------------------------------------------
function SOPsSection({ canRecommend, userRole, isOwner }) {
  const [rows, setRows] = useState([]);
  const [sectionsVisible, setSectionsVisible] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recommendOpen, setRecommendOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/knowledge-hub/sops');
      setRows(r.data.rows || []);
      setSectionsVisible(r.data.sections_visible || []);
    } catch (e) { toast.error(parseErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const design = rows.filter(r => r.section === 'design');
  const accounts = rows.filter(r => r.section === 'accounts');

  return (
    <div className="space-y-4" data-testid="kh-sops">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
          {sectionsVisible.length === 2 ? 'Both folders visible to you.'
            : sectionsVisible.length === 1 ? `You have access to ${sectionsVisible[0]} SOPs.`
            : 'Your role has no SOP folders visible yet.'}
        </div>
        {canRecommend && (
          <Button size="sm" onClick={() => setRecommendOpen(true)} data-testid="kh-sop-recommend-btn">
            <Plus size={13} className="mr-1.5" />{isOwner ? 'Add SOP' : 'Recommend SOP'}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="card p-8 text-center text-[hsl(var(--foreground-muted))]">Loading SOPs…</div>
      ) : (
        <div className="space-y-4">
          {sectionsVisible.includes('design') && (
            <SopFolder title="Design SOPs" icon={BookOpen} items={design} isOwner={isOwner} onChange={load} />
          )}
          {sectionsVisible.includes('accounts') && (
            <SopFolder title="Accounts SOPs" icon={ShieldCheck} items={accounts} isOwner={isOwner} onChange={load} />
          )}
        </div>
      )}

      <SOPRecommendDialog open={recommendOpen} onOpenChange={setRecommendOpen}
                          onSaved={load} sectionsVisible={sectionsVisible}
                          autoApprove={isOwner} />
    </div>
  );
}

function SopFolder({ title, icon: Icon, items, isOwner, onChange }) {
  const del = async (id) => {
    if (!window.confirm('Delete this SOP link?')) return;
    try { await api.delete(`/knowledge-hub/sops/${id}`); toast.success('Deleted.'); onChange(); }
    catch (e) { toast.error(parseErr(e)); }
  };
  return (
    <div className="card p-4 elev-1" data-testid={`kh-sop-folder-${title.split(' ')[0].toLowerCase()}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-[hsl(var(--primary))]" />
        <h3 className="text-[13.5px] font-semibold">{title}</h3>
        <span className="text-[11px] text-[hsl(var(--foreground-muted))]">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="py-6 text-center text-[12.5px] text-[hsl(var(--foreground-muted))]">
          No approved SOPs in this folder yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map(it => (
            <div key={it.id} className="border rounded-md p-3 hover:shadow-sm transition-all bg-[hsl(var(--surface-1))]"
                 style={{ borderColor: 'hsl(var(--border))' }}
                 data-testid={`kh-sop-card-${it.id}`}>
              <a href={it.url} target="_blank" rel="noreferrer"
                 className="font-semibold text-[13px] text-[hsl(var(--primary))] hover:underline flex items-start gap-1.5">
                {it.title}
                <ExternalLink size={11} className="mt-0.5 flex-none" />
              </a>
              {it.description && (
                <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-1 line-clamp-3">{it.description}</div>
              )}
              <div className="mt-2 flex items-center justify-between text-[10.5px] text-[hsl(var(--foreground-subtle))]">
                <span>by {it.submitted_by}</span>
                {isOwner && (
                  <button onClick={() => del(it.id)} className="hover:text-[hsl(var(--danger))]"
                          data-testid={`kh-sop-delete-${it.id}`}>
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SOPRecommendDialog({ open, onOpenChange, onSaved, sectionsVisible, autoApprove }) {
  const [form, setForm] = useState({ title: '', section: '', url: '', description: '', reason: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ title: '', section: sectionsVisible[0] || 'design', url: '', description: '', reason: '' }); }, [open, sectionsVisible]);
  const submit = async () => {
    if (!form.title.trim() || !form.url.trim() || !form.section) {
      toast.error('Title, section, and URL are required.'); return;
    }
    setBusy(true);
    try {
      await api.post('/knowledge-hub/sops', form);
      toast.success(autoApprove ? 'SOP added.' : 'Submitted. The Owner will review it shortly.');
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" data-testid="kh-sop-recommend-dialog">
        <DialogHeader>
          <DialogTitle>{autoApprove ? 'Add SOP' : 'Recommend a new SOP'}</DialogTitle>
          <DialogDescription>
            {autoApprove
              ? 'You are the Owner — new SOPs go live instantly.'
              : 'Your recommendation goes to the Owner for approval. You will be notified either way.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Title" required>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                   placeholder="e.g. Handover Checklist v2" data-testid="kh-sop-title" />
          </Field>
          <Field label="Section" required>
            <Select value={form.section} onValueChange={v => setForm(f => ({ ...f, section: v }))}>
              <SelectTrigger data-testid="kh-sop-section"><SelectValue placeholder="Choose folder" /></SelectTrigger>
              <SelectContent>
                {sectionsVisible.includes('design') && <SelectItem value="design">Design SOPs</SelectItem>}
                {sectionsVisible.includes('accounts') && <SelectItem value="accounts">Accounts SOPs</SelectItem>}
              </SelectContent>
            </Select>
          </Field>
          <Field label="URL" required>
            <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                   placeholder="https://…" data-testid="kh-sop-url" />
          </Field>
          <Field label="Short description">
            <Textarea rows={2} value={form.description}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="One-line summary of what this SOP covers." data-testid="kh-sop-desc" />
          </Field>
          {!autoApprove && (
            <Field label="Why this SOP matters">
              <Textarea rows={2} value={form.reason}
                        onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                        placeholder="Helps the Owner review quickly." data-testid="kh-sop-reason" />
            </Field>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="kh-sop-submit">
            {busy ? 'Submitting…' : (autoApprove ? 'Add SOP' : 'Recommend')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Templates
// ------------------------------------------------------------------
function TemplatesSection({ isApprover, userId }) {
  const [data, setData] = useState({ approved: [], pending: [] });
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/knowledge-hub/templates');
      setData({ approved: r.data.approved || [], pending: r.data.pending || [] });
    } catch (e) { toast.error(parseErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await api.delete(`/knowledge-hub/templates/${id}`); toast.success('Deleted.'); load(); }
    catch (e) { toast.error(parseErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="kh-templates">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
          {isApprover ? 'You approve incoming submissions.' : 'Approvals happen within a day.'}
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="kh-template-add-btn">
          <Plus size={13} className="mr-1.5" />Add template
        </Button>
      </div>

      {loading ? (
        <div className="card p-8 text-center text-[hsl(var(--foreground-muted))]">Loading…</div>
      ) : (
        <>
          {data.pending.length > 0 && (
            <div className="card p-4 elev-1" style={{ borderColor: 'hsl(var(--warning))', borderWidth: 1 }}
                 data-testid="kh-template-pending">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={14} className="text-[hsl(var(--warning))]" />
                <h3 className="text-[13.5px] font-semibold">
                  {isApprover ? 'Pending your review' : 'Your pending submissions'}
                </h3>
                <span className="text-[11px] text-[hsl(var(--foreground-muted))]">{data.pending.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.pending.map(it => (
                  <TemplateCard key={it.id} item={it} isApprover={isApprover} userId={userId}
                                onDelete={del} onAction={load} pending />
                ))}
              </div>
            </div>
          )}
          <div className="card p-4 elev-1" data-testid="kh-template-approved">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={14} className="text-[hsl(var(--success))]" />
              <h3 className="text-[13.5px] font-semibold">Approved templates</h3>
              <span className="text-[11px] text-[hsl(var(--foreground-muted))]">{data.approved.length}</span>
            </div>
            {data.approved.length === 0 ? (
              <div className="py-6 text-center text-[12.5px] text-[hsl(var(--foreground-muted))]">
                No approved templates yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.approved.map(it => (
                  <TemplateCard key={it.id} item={it} isApprover={isApprover} userId={userId}
                                onDelete={del} />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <TemplateAddDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load} isApprover={isApprover} />
    </div>
  );
}

function TemplateCard({ item, isApprover, userId, onDelete, onAction, pending }) {
  const approve = async () => {
    try { await api.post(`/knowledge-hub/templates/${item.id}/approve`); toast.success('Approved.'); onAction && onAction(); }
    catch (e) { toast.error(parseErr(e)); }
  };
  const reject = async () => {
    const reason = window.prompt('Reason for rejection (optional):') || '';
    try { await api.post(`/knowledge-hub/templates/${item.id}/reject`, { reason }); toast.success('Rejected.'); onAction && onAction(); }
    catch (e) { toast.error(parseErr(e)); }
  };
  const canDelete = isApprover || (item.submitted_by_id === userId && item.approval_status === 'pending');
  return (
    <div className="border rounded-md p-3 bg-[hsl(var(--surface-1))]"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid={`kh-template-card-${item.id}`}>
      <a href={item.url} target="_blank" rel="noreferrer"
         className="font-semibold text-[13px] text-[hsl(var(--primary))] hover:underline flex items-start gap-1.5">
        {item.title}
        <ExternalLink size={11} className="mt-0.5 flex-none" />
      </a>
      {item.description && (
        <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-1 line-clamp-2">{item.description}</div>
      )}
      <div className="mt-2 flex items-center justify-between text-[10.5px] text-[hsl(var(--foreground-subtle))]">
        <span>by {item.submitted_by}</span>
        <div className="flex items-center gap-1">
          {pending && isApprover && (
            <>
              <button onClick={approve} className="px-1.5 py-0.5 rounded hover:bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]"
                      data-testid={`kh-template-approve-${item.id}`}>
                <Check size={11} />
              </button>
              <button onClick={reject} className="px-1.5 py-0.5 rounded hover:bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]"
                      data-testid={`kh-template-reject-${item.id}`}>
                <X size={11} />
              </button>
            </>
          )}
          {canDelete && !pending && (
            <button onClick={() => onDelete(item.id)} className="hover:text-[hsl(var(--danger))]"
                    data-testid={`kh-template-delete-${item.id}`}>
              <Trash2 size={11} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function TemplateAddDialog({ open, onOpenChange, onSaved, isApprover }) {
  const [form, setForm] = useState({ title: '', url: '', description: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ title: '', url: '', description: '' }); }, [open]);
  const submit = async () => {
    if (!form.title.trim() || !form.url.trim()) { toast.error('Title and URL required.'); return; }
    setBusy(true);
    try {
      await api.post('/knowledge-hub/templates', form);
      toast.success(isApprover ? 'Template added.' : 'Submitted. Ops or Owner will review.');
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]" data-testid="kh-template-add-dialog">
        <DialogHeader>
          <DialogTitle>{isApprover ? 'Add email template' : 'Submit email template'}</DialogTitle>
          <DialogDescription>
            {isApprover ? 'As Ops/Owner it auto-approves.' : 'A Design Ops or Owner reviews before it becomes active.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Title" required>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                   placeholder="e.g. Client kickoff follow-up" data-testid="kh-template-title" />
          </Field>
          <Field label="URL" required>
            <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                   placeholder="https://…" data-testid="kh-template-url" />
          </Field>
          <Field label="Description">
            <Textarea rows={2} value={form.description}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="One-line: when to use it." data-testid="kh-template-desc" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="kh-template-submit">
            {busy ? 'Submitting…' : (isApprover ? 'Add' : 'Submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Design resources
// ------------------------------------------------------------------
function ResourcesSection({ canManage }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, item: null });

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/knowledge-hub/resources'); setRows(r.data.rows || []); }
    catch (e) { toast.error(parseErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm('Delete this resource?')) return;
    try { await api.delete(`/knowledge-hub/resources/${id}`); toast.success('Deleted.'); load(); }
    catch (e) { toast.error(parseErr(e)); }
  };

  return (
    <div className="space-y-3" data-testid="kh-resources">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
          {canManage ? 'You can add, edit and delete resources.' : 'Curated by Design Ops and the Owner.'}
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setDialog({ open: true, item: null })} data-testid="kh-resource-add-btn">
            <Plus size={13} className="mr-1.5" />Add resource
          </Button>
        )}
      </div>
      {loading ? (
        <div className="card p-8 text-center text-[hsl(var(--foreground-muted))]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center" data-testid="kh-resources-empty">
          <div className="text-[14px] font-semibold">No resources yet</div>
          <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] mt-1">
            {canManage ? 'Click "Add resource" to link the studio\'s first font pack, illustration library, or plug-in.'
                       : 'Design Ops will add resources as the studio builds a shared library.'}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map(it => (
            <div key={it.id} className="card card-hover p-3" data-testid={`kh-resource-card-${it.id}`}>
              <a href={it.url} target="_blank" rel="noreferrer"
                 className="font-semibold text-[13px] text-[hsl(var(--primary))] hover:underline flex items-start gap-1.5">
                {it.title}
                <ExternalLink size={11} className="mt-0.5 flex-none" />
              </a>
              {it.description && (
                <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] mt-1 line-clamp-3">{it.description}</div>
              )}
              {it.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {it.tags.map(t => (
                    <span key={t} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] bg-[hsl(var(--surface-2))]">
                      <Tag size={8} />{t}
                    </span>
                  ))}
                </div>
              )}
              {canManage && (
                <div className="mt-2 flex items-center gap-1 justify-end">
                  <button onClick={() => setDialog({ open: true, item: it })}
                          className="w-6 h-6 grid place-items-center rounded hover:bg-[hsl(var(--surface-2))]"
                          data-testid={`kh-resource-edit-${it.id}`}>
                    <Pencil size={11} />
                  </button>
                  <button onClick={() => del(it.id)}
                          className="w-6 h-6 grid place-items-center rounded hover:bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]"
                          data-testid={`kh-resource-delete-${it.id}`}>
                    <Trash2 size={11} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <ResourceDialog open={dialog.open} item={dialog.item}
                      onOpenChange={(v) => setDialog(d => ({ ...d, open: v }))}
                      onSaved={load} />
    </div>
  );
}

function ResourceDialog({ open, item, onOpenChange, onSaved }) {
  const isEdit = !!item;
  const [form, setForm] = useState({ title: '', url: '', description: '', tags: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (item) setForm({ title: item.title || '', url: item.url || '',
                        description: item.description || '', tags: (item.tags || []).join(', ') });
    else setForm({ title: '', url: '', description: '', tags: '' });
  }, [open, item]);
  const submit = async () => {
    if (!form.title.trim() || !form.url.trim()) { toast.error('Title and URL required.'); return; }
    setBusy(true);
    try {
      const body = { title: form.title, url: form.url, description: form.description,
                     tags: form.tags.split(',').map(s => s.trim()).filter(Boolean) };
      if (isEdit) await api.patch(`/knowledge-hub/resources/${item.id}`, body);
      else await api.post('/knowledge-hub/resources', body);
      toast.success(isEdit ? 'Updated.' : 'Added.');
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]" data-testid="kh-resource-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit resource' : 'Add design resource'}</DialogTitle>
          <DialogDescription>Everyone can browse resources — no approval needed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Title" required>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                   placeholder="e.g. Google Fonts" data-testid="kh-resource-title" />
          </Field>
          <Field label="URL" required>
            <Input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                   placeholder="https://…" data-testid="kh-resource-url" />
          </Field>
          <Field label="Description">
            <Textarea rows={2} value={form.description}
                      onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="One line: what and when to use it." data-testid="kh-resource-desc" />
          </Field>
          <Field label="Tags">
            <Input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                   placeholder="type, illustration, colour (comma-separated)"
                   data-testid="kh-resource-tags" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy} data-testid="kh-resource-submit">
            {busy ? 'Saving…' : (isEdit ? 'Save' : 'Add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Pending approvals
// ------------------------------------------------------------------
function PendingApprovalsSection({ isOwner, isTemplateApprover }) {
  const [sops, setSops] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const promises = [];
      if (isOwner) promises.push(api.get('/knowledge-hub/sops/pending'));
      else promises.push(Promise.resolve({ data: { rows: [] } }));
      promises.push(api.get('/knowledge-hub/templates'));
      const [sopResp, tmplResp] = await Promise.all(promises);
      setSops(sopResp.data.rows || []);
      setTemplates((tmplResp.data.pending || []));
    } catch (e) { toast.error(parseErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approveSop = async (id) => {
    try { await api.post(`/knowledge-hub/sops/${id}/approve`); toast.success('SOP approved.'); load(); }
    catch (e) { toast.error(parseErr(e)); }
  };
  const rejectSop = async (id) => {
    const reason = window.prompt('Reason (optional):') || '';
    try { await api.post(`/knowledge-hub/sops/${id}/reject`, { reason }); toast.success('SOP rejected.'); load(); }
    catch (e) { toast.error(parseErr(e)); }
  };
  const approveTemplate = async (id) => {
    try { await api.post(`/knowledge-hub/templates/${id}/approve`); toast.success('Template approved.'); load(); }
    catch (e) { toast.error(parseErr(e)); }
  };
  const rejectTemplate = async (id) => {
    const reason = window.prompt('Reason (optional):') || '';
    try { await api.post(`/knowledge-hub/templates/${id}/reject`, { reason }); toast.success('Template rejected.'); load(); }
    catch (e) { toast.error(parseErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="kh-pending">
      {loading ? (
        <div className="card p-8 text-center text-[hsl(var(--foreground-muted))]">Loading queue…</div>
      ) : (
        <>
          {isOwner && (
            <div className="card p-4 elev-1">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen size={14} className="text-[hsl(var(--primary))]" />
                <h3 className="text-[13.5px] font-semibold">Pending SOPs</h3>
                <span className="text-[11px] text-[hsl(var(--foreground-muted))]">{sops.length}</span>
              </div>
              {sops.length === 0
                ? <div className="py-4 text-center text-[12px] text-[hsl(var(--foreground-muted))]">Nothing in the queue. Nice.</div>
                : sops.map(it => (
                    <PendingRow key={it.id} item={it} onApprove={() => approveSop(it.id)}
                                onReject={() => rejectSop(it.id)}
                                extra={<span className="chip status-in-progress">{it.section}</span>} />
                  ))}
            </div>
          )}
          {isTemplateApprover && (
            <div className="card p-4 elev-1">
              <div className="flex items-center gap-2 mb-2">
                <Mail size={14} className="text-[hsl(var(--primary))]" />
                <h3 className="text-[13.5px] font-semibold">Pending email templates</h3>
                <span className="text-[11px] text-[hsl(var(--foreground-muted))]">{templates.length}</span>
              </div>
              {templates.length === 0
                ? <div className="py-4 text-center text-[12px] text-[hsl(var(--foreground-muted))]">All clear.</div>
                : templates.map(it => (
                    <PendingRow key={it.id} item={it} onApprove={() => approveTemplate(it.id)}
                                onReject={() => rejectTemplate(it.id)} />
                  ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PendingRow({ item, onApprove, onReject, extra }) {
  return (
    <div className="flex items-start gap-3 py-2 border-t first:border-t-0"
         style={{ borderColor: 'hsl(var(--border))' }}
         data-testid={`kh-pending-row-${item.id}`}>
      <div className="flex-1 min-w-0">
        <a href={item.url} target="_blank" rel="noreferrer"
           className="font-semibold text-[13px] text-[hsl(var(--primary))] hover:underline flex items-center gap-1">
          {item.title}<ExternalLink size={10} />
        </a>
        <div className="text-[11px] text-[hsl(var(--foreground-muted))] mt-0.5">
          Submitted by {item.submitted_by} · {fmtDateTime(item.created_at)}
          {extra && <span className="ml-2">{extra}</span>}
        </div>
        {item.description && (
          <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-1">{item.description}</div>
        )}
        {item.reason && (
          <div className="text-[11.5px] italic text-[hsl(var(--foreground-muted))] mt-0.5">Reason: {item.reason}</div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button onClick={onApprove} className="px-2 py-1 rounded flex items-center gap-1 text-[hsl(var(--success))] hover:bg-[hsl(var(--success-bg))] text-[11.5px] font-semibold"
                data-testid={`kh-pending-approve-${item.id}`}>
          <ThumbsUp size={11} />Approve
        </button>
        <button onClick={onReject} className="px-2 py-1 rounded flex items-center gap-1 text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))] text-[11.5px] font-semibold"
                data-testid={`kh-pending-reject-${item.id}`}>
          <ThumbsDown size={11} />Reject
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// How-to (permission aware)
// ------------------------------------------------------------------
function HowToSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const r = await api.get('/knowledge-hub/how-to'); setData(r.data); }
      catch (e) { toast.error(parseErr(e)); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <div className="card p-8 text-center text-[hsl(var(--foreground-muted))]">Loading guide…</div>;
  if (!data) return null;
  return (
    <div className="space-y-3" data-testid="kh-how-to">
      <div className="card p-4 elev-1">
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold mb-1">
          Written for {data.role}
        </div>
        <div className="text-[13px] text-[hsl(var(--foreground-muted))]">
          Everything on this page is auto-generated from the current permission rules. If your role changes, this guide will reflect it on your next visit.
        </div>
      </div>
      {data.sections.map(s => (
        <div key={s.key} className="card p-4 elev-1" data-testid={`kh-how-to-${s.key}`}>
          <h3 className="text-[14px] font-semibold mb-1">{s.title}</h3>
          <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] mb-2">{s.summary}</div>
          {s.permissions?.length > 0 && (
            <ul className="list-disc ml-5 space-y-1 text-[12.5px] mb-2">
              {s.permissions.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          )}
          {s.how_to?.map((h, i) => (
            <div key={i} className="mt-2 p-3 rounded bg-[hsl(var(--surface-2))] text-[12.5px] whitespace-pre-wrap"
                 dangerouslySetInnerHTML={{ __html: renderMarkdownLite(h) }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function renderMarkdownLite(text) {
  return String(text || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br/>');
}

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">
        {label}{required && <span className="text-[hsl(var(--danger))] ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}
